import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TICKER_SEED } from '@/seed/ticker';
import './ticker.css';

/**
 * TICKER-01 — animated market ticker tape.
 *
 * - 9-entry seed (TICKER_SEED) duplicated 3× for seamless infinite scroll (D-23).
 * - Pure CSS @keyframes animation (50s linear infinite); no motion/react (D-03).
 * - Pause triggers:
 *   1. Hover (CSS — `.ticker:hover .ticker-track`)
 *   2. prefers-reduced-motion: reduce (CSS @media — A11Y-04)
 *   3. document.visibilityState === 'hidden' (JS — PERF-05, folded in here per CONTEXT specifics)
 *
 * Phase 3 fold-in of PERF-05 because the visibilitychange hook is trivial
 * here and avoids re-architecture in Phase 5.
 */
export function Ticker(): JSX.Element {
  const { t } = useTranslation();
  const [hidden, setHidden] = useState(false);

  // PERF-05 — pause when document hidden (battery / CPU saving)
  useEffect(() => {
    const onChange = (): void => setHidden(document.visibilityState === 'hidden');
    onChange(); // initial state
    document.addEventListener('visibilitychange', onChange);
    return () => document.removeEventListener('visibilitychange', onChange);
  }, []);

  // 3× duplication for seamless wrap (D-23)
  const items = [...TICKER_SEED, ...TICKER_SEED, ...TICKER_SEED];

  return (
    <div className="ticker" role="marquee" aria-label={t('ticker.live')}>
      <div className="ticker-track" data-pause-visibility={hidden}>
        {items.map((entry, i) => (
          <span key={i} className="ticker-item">
            <span className="ticker-dot" aria-hidden="true" />
            <span>{entry.sym}</span>
            <span style={{ opacity: 0.7 }}>{entry.val}</span>
            <span className={entry.dir === 'up' ? 'ticker-up' : 'ticker-down'}>{entry.d}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

export default Ticker;
